import type { SongStudioSessionRecord } from "./bordonaProgressStorage";
import type { UserProgress } from "./gym/types";
import type { CompletedTask, StudentStats } from "../hooks/useStudentStats";
import type { FavoriteItem } from "../hooks/useStudentFavorites";
import type { PracticeSession } from "../hooks/usePracticeSessions";

export interface DashboardXpBreakdown {
  gym: number;
  tasks: number;
  songs: number;
  explorations: number;
  total: number;
}

export function sessionTimestamp(createdAt: PracticeSession["createdAt"]): number {
  if (!createdAt) return 0;
  if (typeof createdAt === "object" && "toMillis" in createdAt) {
    return (createdAt as { toMillis: () => number }).toMillis();
  }
  return Date.now();
}

export function computeXp(params: {
  gym: UserProgress;
  tasks: CompletedTask[];
  songStudio: SongStudioSessionRecord[];
  firestoreSessions: PracticeSession[];
  stats: StudentStats;
}): DashboardXpBreakdown {
  const gym =
    params.gym.completedExercises.length * 45 +
    Object.values(params.gym.scores).reduce((s, v) => s + Math.round(v * 0.15), 0);

  const tasks = params.tasks.reduce((s, t) => s + Math.round(t.score * 1.2), 0);

  const songStudio = params.songStudio.reduce(
    (s, r) => s + Math.round(r.accuracy * 120) + (r.mode === "learn" ? 30 : 15),
    0
  );

  const firestoreSong = params.firestoreSessions.reduce((s, sess) => {
    const acc =
      sess.attempts > 0 ? Math.round((sess.score / sess.attempts) * 100) : sess.flowScore;
    return s + Math.round(acc * 0.8);
  }, 0);

  const explorations = params.stats.explorations * 8;

  const total = gym + tasks + songStudio + firestoreSong + explorations;
  return { gym, tasks, songs: songStudio + firestoreSong, explorations, total };
}

export function levelFromXp(xp: number): { level: number; current: number; next: number; pct: number } {
  const level = Math.max(1, Math.floor(Math.sqrt(xp / 80)) + 1);
  const xpForLevel = (l: number) => Math.pow(l - 1, 2) * 80;
  const current = xp - xpForLevel(level);
  const next = xpForLevel(level + 1) - xpForLevel(level);
  const pct = next > 0 ? Math.min(100, Math.round((current / next) * 100)) : 100;
  return { level, current, next, pct };
}

export function badgeList(params: {
  gym: UserProgress;
  tasks: CompletedTask[];
  favorites: FavoriteItem[];
  stats: StudentStats;
  songCount: number;
}): { id: string; label: string; earned: boolean; hint: string }[] {
  const gymDone = params.gym.completedExercises.length;
  return [
    {
      id: "first-note",
      label: "Primera nota",
      earned: params.stats.explorations > 0 || gymDone > 0,
      hint: "Explora cualquier módulo del mástil",
    },
    {
      id: "gym-10",
      label: "10 ejercicios",
      earned: gymDone >= 10,
      hint: "Completa ejercicios en el Gimnasio",
    },
    {
      id: "gym-50",
      label: "Maestro del método",
      earned: gymDone >= 50,
      hint: "50 ejercicios del gimnasio",
    },
    {
      id: "streak-3",
      label: "Racha 3 días",
      earned: params.stats.currentStreak >= 3 || params.gym.streakDays >= 3,
      hint: "Practica tres días seguidos",
    },
    {
      id: "collector",
      label: "Coleccionista",
      earned: params.favorites.length >= 5,
      hint: "Guarda 5 escalas, acordes o modos",
    },
    {
      id: "songbird",
      label: "Cancionero",
      earned: params.songCount >= 1,
      hint: "Sube o estudia una canción",
    },
    {
      id: "task-master",
      label: "Tarea perfecta",
      earned: params.tasks.some((t) => t.score >= 95),
      hint: "≥95% en una tarea del profesor",
    },
  ];
}
